import type { FC } from 'react';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { Aligner } from '@alfa-bank/site-aligner';
import { ButtonDesktop as Button } from '@alfa-bank/site-button-v2/desktop';
import { Link } from '@alfa-bank/site-link';
import { NamedContainer } from '@alfa-bank/site-named-container';
import { BlockDesktop as Block } from '@alfa-bank/site-block/desktop';
import { List } from '@alfa-bank/site-lists';
import { TabsTagsDesktop as TabsTags } from '@alfa-bank/site-tabs/tabs-tags-desktop';
import { Tab } from '@alfa-bank/site-tabs/tab';
import { Hr } from '@alfa-bank/site-hr';
import { AnalyticsEventSender } from '@alfa-bank/site-analytics-event-sender/analytics-event-sender';

import { redirectUserToEsia, selectEsiaLoginStep } from '../../store';
import { GosuslugiIcon } from '../../components/gosuslugi-icon';
import { OFFICES_LINK } from '../../config';
import { sendEventSmev } from '../../helpers/proxy-send-event';

import type { TEsiaLoginStepDesktop } from './model';
import { defaultEsiaLoginSettingsDesktop } from './config/default-values';
import { ESIA_LOGIN_TAB_ID_NO, ESIA_LOGIN_TAB_ID_YES } from './config/constants';
import { analyticsEsiaLoginSmev } from './analytics';

export const EsiaLoginStepDesktop: FC<TEsiaLoginStepDesktop> = ({
    description = defaultEsiaLoginSettingsDesktop.description,
    tabCaption = defaultEsiaLoginSettingsDesktop.tabCaption,
    tabPrimaryButtonText = defaultEsiaLoginSettingsDesktop.tabPrimaryButtonText,
    tabPrimaryList = defaultEsiaLoginSettingsDesktop.tabPrimaryList,
    tabSecondaryButtonText = defaultEsiaLoginSettingsDesktop.tabSecondaryButtonText,
    tabSecondaryDescription = defaultEsiaLoginSettingsDesktop.tabSecondaryDescription,
    title = defaultEsiaLoginSettingsDesktop.title,
}) => {
    const { isLoading } = useSelector(selectEsiaLoginStep);
    const dispatch = useDispatch();

    const handleClick = () => {
        sendEventSmev(analyticsEsiaLoginSmev.buttonESIAClick);
        dispatch(redirectUserToEsia());
    };

    const handleChange = (eventObject: { id: number | string }) => {
        if (eventObject.id === ESIA_LOGIN_TAB_ID_YES) {
            sendEventSmev(analyticsEsiaLoginSmev.tabYesClick);
        }
        if (eventObject.id === ESIA_LOGIN_TAB_ID_NO) {
            sendEventSmev(analyticsEsiaLoginSmev.tabNoClick);
        }
    };

    return (
        <>
            <Aligner>
                <GosuslugiIcon height={64} width={64} />
                <Text styler={{ marginBottom: 16, marginTop: 24 }} tag="h2" view="medium">
                    {title}
                </Text>
                <Text styler={{ marginTop: 0, marginBottom: 0 }} view="primary-medium">
                    {description}
                </Text>
            </Aligner>
            <Hr styler={{ marginBottom: 32, marginTop: 32 }} theme="light" />
            <Text
                kind="caption"
                styler={{ marginTop: 0, marginBottom: 12 }}
                view="component-secondary"
            >
                {tabCaption}
            </Text>
            <TabsTags inline onChange={handleChange} size="xs">
                <Tab id={ESIA_LOGIN_TAB_ID_YES} keepMounted title="Да">
                    <Block notPadded styler={{ marginTop: 32 }}>
                        <List indentType="12" marker="•" type="ul">
                            {tabPrimaryList.map(item => (
                                <Text
                                    key={item}
                                    styler={{ marginTop: 0, marginBottom: 0 }}
                                    view="primary-medium"
                                >
                                    {item}
                                </Text>
                            ))}
                        </List>
                        <Aligner styler={{ marginTop: 24 }}>
                            <Button
                                loading={isLoading}
                                onClick={handleClick}
                                size="s"
                                view="secondary"
                            >
                                <NamedContainer name="leftAddons">
                                    <GosuslugiIcon height={24} width={24} />
                                </NamedContainer>
                                {tabPrimaryButtonText}
                            </Button>
                        </Aligner>
                    </Block>
                </Tab>
                <Tab id={ESIA_LOGIN_TAB_ID_NO} keepMounted title="Нет">
                    <Block notPadded styler={{ marginTop: 32 }}>
                        <Text styler={{ marginTop: 0, marginBottom: 24 }} view="primary-medium">
                            {tabSecondaryDescription}
                        </Text>
                        <Aligner>
                            <AnalyticsEventSender
                                $events={{
                                    onClick: {
                                        data: analyticsEsiaLoginSmev.buttonFindOfficeClick,
                                    },
                                }}
                            >
                                <Link border="hidden" href={OFFICES_LINK} target="_blank">
                                    <Button size="s" view="tertiary">
                                        {tabSecondaryButtonText}
                                    </Button>
                                </Link>
                            </AnalyticsEventSender>
                        </Aligner>
                    </Block>
                </Tab>
            </TabsTags>
        </>
    );
};
